
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { COOKIE_CONFIG, DOMAIN_CONFIG } from "@/integrations/supabase/config";

// Helper to get cross-domain session from cookie
const getCrossDomainSession = (): any => {
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [key, ...valueParts] = cookie.trim().split("=");
      if (key === COOKIE_CONFIG.crossDomainName) {
        const value = valueParts.join("=");
        return JSON.parse(decodeURIComponent(value));
      }
    }
  } catch (error) {
    console.warn("[useSuperadminRole] Error reading cross-domain session:", error);
  }
  return null;
};

// Helper to update cross-domain session with superadmin status
const updateCrossDomainSession = (isSuperadmin: boolean): void => {
  try {
    const sessionData = getCrossDomainSession();
    if (sessionData) {
      sessionData.is_superadmin = isSuperadmin;
      sessionData.last_updated = Date.now();

      const cookieOptions = [
        `${COOKIE_CONFIG.crossDomainName}=${encodeURIComponent(
          JSON.stringify(sessionData)
        )}`,
        DOMAIN_CONFIG.parentDomain ? `domain=${DOMAIN_CONFIG.parentDomain}` : "",
        `path=/`,
        `max-age=${COOKIE_CONFIG.maxAge}`,
        COOKIE_CONFIG.secure ? "secure" : "",
        `samesite=${COOKIE_CONFIG.sameSite}`,
      ]
        .filter(Boolean)
        .join("; ");

      document.cookie = cookieOptions;

      // Also update localStorage
      localStorage.setItem("cross_domain_session", JSON.stringify(sessionData));
    }
  } catch (error) {
    console.warn("[useSuperadminRole] Error updating cross-domain session:", error);
  }
};

export function useSuperadminRole() {
  const [isSuperadmin, setIsSuperadmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasNetworkError, setHasNetworkError] = useState<boolean>(false);

  const checkSuperAdminStatus = async (): Promise<boolean> => {
    setIsLoading(true);
    setHasNetworkError(false);
    
    // First, check cross-domain session for immediate result
    const crossDomainSession = getCrossDomainSession();
    if (crossDomainSession?.is_superadmin !== undefined) {
      const cachedStatus = Boolean(crossDomainSession.is_superadmin);
      setIsSuperadmin(cachedStatus);
      setIsLoading(false);
      // Still verify with RPC in background
    }
    
    try {
      const { data, error } = await supabase.rpc("is_superadmin");

      if (error) {
        setHasNetworkError(true);
        // If we have cached status, keep using it
        if (crossDomainSession?.is_superadmin === undefined) {
          setIsSuperadmin(false);
        }
        return crossDomainSession?.is_superadmin ?? false;
      }

      if (typeof data !== "boolean") {
        return crossDomainSession?.is_superadmin ?? false;
      }
      
      setIsSuperadmin(data);
      // Store in cross-domain session for other domains to access
      updateCrossDomainSession(data);
      return data;
    } catch (err) {
      setHasNetworkError(true);
      // If we have cached status, keep using it
      if (crossDomainSession?.is_superadmin === undefined) {
        setIsSuperadmin(false);
      }
      return crossDomainSession?.is_superadmin ?? false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCacheAndRecheck = async (): Promise<boolean> => {
    // Clear cross-domain cache
    try {
      const sessionData = getCrossDomainSession();
      if (sessionData) {
        delete sessionData.is_superadmin;
        const cookieOptions = [
          `${COOKIE_CONFIG.crossDomainName}=${encodeURIComponent(
            JSON.stringify(sessionData)
          )}`,
          DOMAIN_CONFIG.parentDomain ? `domain=${DOMAIN_CONFIG.parentDomain}` : "",
          `path=/`,
          `max-age=${COOKIE_CONFIG.maxAge}`,
          COOKIE_CONFIG.secure ? "secure" : "",
          `samesite=${COOKIE_CONFIG.sameSite}`,
        ]
          .filter(Boolean)
          .join("; ");
        document.cookie = cookieOptions;
        localStorage.setItem("cross_domain_session", JSON.stringify(sessionData));
      }
    } catch (error) {
      console.warn("[useSuperadminRole] Error clearing cross-domain cache:", error);
    }
    return await checkSuperAdminStatus();
  };

  const verifyDatabaseSuperadminStatus = async (): Promise<boolean> => {
    // Direct database check - same as checkSuperAdminStatus for now
    return await checkSuperAdminStatus();
  };

  useEffect(() => {
    // Check cross-domain session first for immediate result
    const crossDomainSession = getCrossDomainSession();
    if (crossDomainSession?.is_superadmin !== undefined) {
      setIsSuperadmin(Boolean(crossDomainSession.is_superadmin));
      setIsLoading(false);
    }
    
    // Then verify with RPC call
    checkSuperAdminStatus();
  }, []);

  return {
    isSuperadmin,
    isLoading,
    hasNetworkError,
    clearCacheAndRecheck,
    verifyDatabaseSuperadminStatus,
  };
}
